import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 体育科学 - 横屏「运动分析」风格
 *
 * 核心亮点：
 * - SVG 雷达图（能力六维图）
 * - 运动数据统计卡片
 * - 人体运动轨迹弧线
 * - 对比数据面板
 * - 训练建议 + 关键洞察
 *
 * 尺寸：1920x1080 横屏
 */

interface RadarStat {
  label: string;
  value: number;
  max: number;
}

interface PerformanceData {
  label: string;
  value: string;
  unit: string;
  icon: string;
  color: string;
}

interface SportsAnalysisTemplateProps {
  sport: string;
  subtitle: string;
  athlete: string;
  radarStats: RadarStat[];
  performanceData: PerformanceData[];
  keyInsight: string;
  trainingTips: string[];
  comparison: { label: string; pro: string; amateur: string }[];
}

const DEFAULT_PROPS: SportsAnalysisTemplateProps = {
  sport: '100 米短跑运动分析',
  subtitle: 'Sprint Biomechanics Breakdown',
  athlete: '精英短跑运动员',
  radarStats: [
    { label: '爆发力', value: 92, max: 100 },
    { label: '步频', value: 88, max: 100 },
    { label: '步幅', value: 85, max: 100 },
    { label: '反应时', value: 90, max: 100 },
    { label: '加速能力', value: 94, max: 100 },
    { label: '最高速度', value: 96, max: 100 },
  ],
  performanceData: [
    { label: '最高时速', value: '44.72', unit: 'km/h', icon: '⚡', color: '#ef4444' },
    { label: '反应时间', value: '0.12', unit: 's', icon: '🧠', color: '#818cf8' },
    { label: '步频峰值', value: '4.8', unit: '步/s', icon: '🦶', color: '#22c55e' },
    { label: '触地时间', value: '80', unit: 'ms', icon: '⏱️', color: '#f59e0b' },
  ],
  keyInsight: '精英短跑运动员的触地时间仅 80ms，而业余跑者约 200ms——减少触地时间是提速关键',
  trainingTips: [
    '高抬腿练习提升步频',
    '深蹲跳增强下肢爆发力',
    '弹力带阻力跑改善加速姿态',
    '反应球训练缩短起跑反应时',
  ],
  comparison: [
    { label: '100m 成绩', pro: '9.58s', amateur: '12-13s' },
    { label: '最高时速', pro: '44.7 km/h', amateur: '28-32 km/h' },
    { label: '步幅', pro: '2.44m', amateur: '1.8-2.0m' },
  ],
};

export const SportsAnalysisTemplate: React.FC<Partial<SportsAnalysisTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  const radarCx = 360;
  const radarCy = 320;
  const radarR = 230;
  const radarDraw = interpolate(frame, [40, 100], [0, 1], { extrapolateRight: 'clamp' });
  const n = p.radarStats.length;

  const radarPoints = p.radarStats.map((stat, i) => {
    const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
    const r = (stat.value / stat.max) * radarR * radarDraw;
    return {
      x: radarCx + r * Math.cos(angle),
      y: radarCy + r * Math.sin(angle),
      labelX: radarCx + (radarR + 40) * Math.cos(angle),
      labelY: radarCy + (radarR + 40) * Math.sin(angle),
    };
  });

  const radarPolygon = radarPoints.map((pt) => `${pt.x},${pt.y}`).join(' ');

  const dataStart = 60;
  const dataInterval = 25;
  const compStart = dataStart + p.performanceData.length * dataInterval + 40;
  const tipStart = compStart + 80;
  const insightStart = tipStart + p.trainingTips.length * 25 + 20;
  const insightOpacity = interpolate(frame, [insightStart, insightStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  const runPhase = (frame * 0.1) % (Math.PI * 2);

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #0f0f0f 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ position: 'absolute', top: 28, left: 50, right: 50, display: 'flex', alignItems: 'center', gap: 18, opacity: headerProgress }}>
        <div style={{ padding: '6px 18px', background: '#22c55e18', border: '1px solid #22c55e40', borderRadius: 10, fontSize: 18, color: '#22c55e', fontWeight: 700, whiteSpace: 'nowrap' }}>
          🏃 SPORTS SCIENCE
        </div>
        <h1 style={{ fontSize: 46, fontWeight: 900, margin: 0, whiteSpace: 'nowrap' }}>{p.sport}</h1>
        <p style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', margin: 0, whiteSpace: 'nowrap' }}>{p.subtitle}</p>
      </div>

      {/* ========== RADAR CHART (left) ========== */}
      <svg style={{ position: 'absolute', left: 30, top: 120 }} width="740" height="680" viewBox="0 0 740 680">
        {/* Grid rings */}
        {[0.25, 0.5, 0.75, 1].map((t) => (
          <polygon
            key={t}
            points={Array.from({ length: n }).map((_, i) => {
              const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
              return `${radarCx + radarR * t * Math.cos(angle)},${radarCy + radarR * t * Math.sin(angle)}`;
            }).join(' ')}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="1"
          />
        ))}

        {/* Axis lines */}
        {Array.from({ length: n }).map((_, i) => {
          const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
          return (
            <line
              key={i}
              x1={radarCx}
              y1={radarCy}
              x2={radarCx + radarR * Math.cos(angle)}
              y2={radarCy + radarR * Math.sin(angle)}
              stroke="rgba(255,255,255,0.06)"
              strokeWidth="1"
            />
          );
        })}

        {/* Data polygon */}
        <polygon
          points={radarPolygon}
          fill="#22c55e"
          fillOpacity="0.12"
          stroke="#22c55e"
          strokeWidth="2.5"
          opacity={radarDraw}
        />

        {/* Data points */}
        {radarPoints.map((pt, i) => (
          <g key={i} opacity={radarDraw}>
            <circle cx={pt.x} cy={pt.y} r="6" fill="#22c55e" />
            <circle cx={pt.x} cy={pt.y} r="12" fill="#22c55e" opacity="0.15" />
          </g>
        ))}

        {/* Labels */}
        {radarPoints.map((pt, i) => (
          <text
            key={`label-${i}`}
            x={pt.labelX}
            y={pt.labelY + 6}
            fill="rgba(255,255,255,0.55)"
            fontSize="22"
            fontWeight="bold"
            textAnchor="middle"
            opacity={radarDraw}
          >
            {p.radarStats[i].label} {p.radarStats[i].value}
          </text>
        ))}

        {/* Running trail arc */}
        <path
          d={`M80,620 Q200,${580 + Math.sin(runPhase) * 20} 360,${570 + Math.sin(runPhase + 1) * 15} Q520,${560 + Math.sin(runPhase + 2) * 20} 660,620`}
          fill="none"
          stroke="#22c55e"
          strokeWidth="2"
          opacity="0.2"
          strokeDasharray="8 4"
        />
        <circle cx={80 + 580 * interpolate(frame % 120, [0, 120], [0, 1])} cy={600 + Math.sin(runPhase) * 15} r="7" fill="#22c55e" opacity="0.6" />
      </svg>

      {/* ========== RIGHT PANEL ========== */}
      <div style={{ position: 'absolute', top: 120, left: 800, right: 50 }}>
        {/* Performance data */}
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 16, letterSpacing: '0.1em', fontWeight: 600 }}>运动表现数据</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {p.performanceData.map((d, i) => {
            const start = dataStart + i * dataInterval;
            const progress = spring({ frame: frame - start, fps, from: 0, to: 1, config: { damping: 12, stiffness: 140, mass: 0.5 } });
            return (
              <div
                key={i}
                style={{
                  padding: '18px 24px',
                  background: `${d.color}08`,
                  borderRadius: 16,
                  border: `1px solid ${d.color}20`,
                  opacity: progress,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                }}
              >
                <div style={{ fontSize: 32, flexShrink: 0 }}>{d.icon}</div>
                <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>{d.label}</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: d.color, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                  {d.value}<span style={{ fontSize: 18, opacity: 0.6, marginLeft: 4 }}>{d.unit}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Comparison table */}
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 14, letterSpacing: '0.1em', fontWeight: 600 }}>🆚 精英 vs 业余对比</div>
          <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: 'rgba(255,255,255,0.03)', padding: '12px 20px' }}>
              <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.35)' }}>指标</span>
              <span style={{ fontSize: 24, color: '#22c55e', fontWeight: 700, textAlign: 'center' }}>精英</span>
              <span style={{ fontSize: 24, color: '#64748b', fontWeight: 700, textAlign: 'center' }}>业余</span>
            </div>
            {p.comparison.map((c, i) => {
              const opacity = interpolate(frame, [compStart + i * 20, compStart + i * 20 + 15], [0, 1], { extrapolateRight: 'clamp' });
              return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.04)', opacity }}>
                  <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.55)' }}>{c.label}</span>
                  <span style={{ fontSize: 22, fontWeight: 800, color: '#22c55e', textAlign: 'center' }}>{c.pro}</span>
                  <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.45)', textAlign: 'center' }}>{c.amateur}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Training tips */}
        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 12, fontWeight: 600 }}>🎯 训练建议</div>
          {p.trainingTips.map((tip, i) => {
            const opacity = interpolate(frame, [tipStart + i * 25, tipStart + i * 25 + 18], [0, 1], { extrapolateRight: 'clamp' });
            return (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, opacity }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#22c55e', marginTop: 12, flexShrink: 0 }} />
                <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>{tip}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Key insight */}
      <div style={{ position: 'absolute', bottom: 30, left: 50, right: 50, opacity: insightOpacity }}>
        <div style={{ padding: '18px 28px', background: '#0d1a12', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 16, textAlign: 'center' }}>
          <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>💡 {p.keyInsight}</span>
        </div>
      </div>

      {/* Progress */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`, height: 3, background: '#22c55e' }} />
    </AbsoluteFill>
  );
};
